import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Typography,
  Space,
  Tag,
  Spin,
  Modal,
  Input,
  message,
  Segmented,
  Alert,
  Drawer,
  Timeline,
  Collapse,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  LinkOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { approvalService, type ApprovalInstanceDTO } from '@/services/approval.service';
import type { RejectionPayload } from '@/services/rejection.service';
import { RejectionReasonModal } from '@/components/rejection/RejectionReasonModal';
import {
  approveViaDomainOrInstance,
  formatApproveSuccessMessage,
  rejectViaDomainOrInstance,
} from '@/services/approval-domain-actions';
import dayjs from 'dayjs';
import { getEntityDetailPath } from '@/config/entityActionPaths';

const { Title, Text } = Typography;

type InboxRow = ApprovalInstanceDTO & {
  amount_display?: string;
  currency?: string;
};

function entityDetailPath(entityType: string, entityId: string): string | null {
  return getEntityDetailPath(entityType, entityId);
}

function inboxReason(row: InboxRow): string | null {
  const meta = row.metadata_json;
  if (!meta || typeof meta !== 'object') return null;
  const reason = meta.reason;
  if (typeof reason !== 'string') return null;
  const trimmed = reason.trim();
  return trimmed || null;
}

function inboxAmountFallback(row: InboxRow): string | null {
  if (row.amount_display) return row.amount_display;
  const meta = row.metadata_json;
  if (!meta || typeof meta !== 'object') return null;
  const over = meta.amount_over ?? meta.amount;
  if (over == null || over === '') return null;
  const n = Number(over);
  if (!Number.isFinite(n)) return null;
  const cur = typeof meta.currency === 'string' && meta.currency.trim() ? meta.currency : 'AED';
  return `${cur} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const INBOX_FILTER_VALUES = ['pending', 'overdue', 'escalated', 'returned'] as const;

type InboxFilter = (typeof INBOX_FILTER_VALUES)[number];
type SortMode = 'impact' | 'due' | 'created';

function entityGroupLabel(entityType: string): string {
  return entityType
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const ApprovalInboxPage: React.FC = () => {
  const { t } = useTranslation(['approvals', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [inboxFilter, setInboxFilter] = useState<InboxFilter>('pending');
  const [sortMode, setSortMode] = useState<SortMode>('impact');
  const inboxFilterOptions = useMemo(
    () =>
      INBOX_FILTER_VALUES.map((value) => ({
        value,
        label: t(value, { ns: 'approvals', defaultValue: value }),
      })),
    [t]
  );
  const [timelineId, setTimelineId] = useState<string | null>(null);
  const [approveModal, setApproveModal] = useState<{
    instanceId: string;
    entityType: string;
    entityId: string;
  } | null>(null);
  const [rejectModal, setRejectModal] = useState<{
    instanceId: string;
    entityType: string;
    entityId: string;
    documentNumber?: string;
  } | null>(null);
  const [remarks, setRemarks] = useState('');

  const { data: summary } = useQuery({
    queryKey: ['notifications', 'summary'],
    queryFn: () => approvalService.notificationSummary(),
    staleTime: 60_000,
  });

  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['approval-inbox', inboxFilter, sortMode],
    queryFn: () => approvalService.inbox(inboxFilter, sortMode) as Promise<InboxRow[]>,
  });

  const { data: timeline = [], isLoading: timelineLoading } = useQuery({
    queryKey: ['notification-timeline', timelineId],
    queryFn: () => approvalService.notificationTimeline(timelineId!),
    enabled: !!timelineId,
  });

  const grouped = useMemo(() => {
    const map = new Map<string, InboxRow[]>();
    for (const r of rows) {
      const key = r.entity_type || 'other';
      const list = map.get(key) || [];
      list.push(r);
      map.set(key, list);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [rows]);

  const approveMutation = useMutation({
    mutationFn: async (payload: {
      instanceId: string;
      entityType: string;
      entityId: string;
      remarks?: string;
    }) =>
      approveViaDomainOrInstance({
        entityType: payload.entityType,
        entityId: payload.entityId,
        instanceId: payload.instanceId,
        remarks: payload.remarks,
      }),
    onSuccess: async (result) => {
      message.success(formatApproveSuccessMessage(result));
      setApproveModal(null);
      setRemarks('');
      await queryClient.invalidateQueries({ queryKey: ['approval-inbox'] });
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax.response?.data?.message || ax.message || 'Action failed');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (payload: {
      instanceId: string;
      entityType: string;
      entityId: string;
      body: RejectionPayload;
    }) =>
      rejectViaDomainOrInstance({
        entityType: payload.entityType,
        entityId: payload.entityId,
        instanceId: payload.instanceId,
        payload: payload.body,
      }),
    onSuccess: async () => {
      message.success('Rejected');
      setRejectModal(null);
      await queryClient.invalidateQueries({ queryKey: ['approval-inbox'] });
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax.response?.data?.message || ax.message || 'Action failed');
    },
  });

  const columns: ColumnsType<InboxRow> = [
    {
      title: 'Document',
      key: 'doc',
      width: 200,
      ellipsis: true,
      render: (_, row) => {
        const path = entityDetailPath(row.entity_type, row.entity_id);
        const label = row.document_number || row.entity_id;
        return path ? (
          <Link to={path} style={{ fontWeight: 500 }}>
            {label}
          </Link>
        ) : (
          <Text code>{label}</Text>
        );
      },
    },
    {
      title: 'Entity type',
      dataIndex: 'entity_type',
      width: 180,
      ellipsis: true,
      render: (v: string) => <Tag>{v.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 140,
      render: (_, row) => inboxAmountFallback(row) || '—',
    },
    {
      title: 'Reason',
      key: 'reason',
      width: 220,
      ellipsis: true,
      render: (_, row) => {
        const reason = inboxReason(row);
        return reason ? (
          <Text type="secondary" ellipsis={{ tooltip: reason }}>
            {reason}
          </Text>
        ) : (
          '—'
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 150,
      ellipsis: true,
      render: (v: string) => <Tag style={{ whiteSpace: 'nowrap' }}>{v}</Tag>,
    },
    {
      title: 'Assigned to',
      key: 'assigned_to',
      width: 180,
      ellipsis: true,
      render: (_, row) => {
        if (row.status !== 'pending_approval' || !row.pendingApproverLabel) return '—';
        const delegator = row.delegated_from_user_id
          ? row.pendingApprovers?.find((a) => a.user_id === row.delegated_from_user_id)
          : undefined;
        return (
          <Space direction="vertical" size={0}>
            {row.delegated_from_user_id ? (
              <Tag color="blue">Delegated from {delegator?.full_name || delegator?.email || 'colleague on leave'}</Tag>
            ) : null}
            <Text type="secondary">{row.pendingApproverLabel}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Step',
      dataIndex: 'current_step_number',
      width: 80,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 360,
      fixed: 'right',
      render: (_, row) => {
        const path = entityDetailPath(row.entity_type, row.entity_id);
        const canAct = row.status === 'pending_approval';
        return (
          <Space wrap size="small">
            {path ? (
              <Button type="link" size="small" icon={<LinkOutlined />} onClick={() => navigate(path)}>
                Open document
              </Button>
            ) : null}
            <Button
              type="link"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setTimelineId(row.id)}
            >
              Timeline
            </Button>
            {canAct ? (
              <>
                <Button
                  type="primary"
                  size="small"
                  icon={<CheckOutlined />}
                  loading={approveMutation.isPending && approveModal?.instanceId === row.id}
                  onClick={() => {
                    setRemarks('');
                    setApproveModal({
                      instanceId: row.id,
                      entityType: row.entity_type,
                      entityId: row.entity_id,
                    });
                  }}
                >
                  {t('approve', { ns: 'approvals' })}
                </Button>
                <Button
                  danger
                  size="small"
                  icon={<CloseOutlined />}
                  loading={rejectMutation.isPending && rejectModal?.instanceId === row.id}
                  onClick={() =>
                    setRejectModal({
                      instanceId: row.id,
                      entityType: row.entity_type,
                      entityId: row.entity_id,
                      documentNumber: row.document_number,
                    })
                  }
                >
                  {t('reject', { ns: 'approvals' })}
                </Button>
              </>
            ) : null}
          </Space>
        );
      },
    },
  ];

  const confirmApprove = () => {
    if (!approveModal) return;
    approveMutation.mutate({
      instanceId: approveModal.instanceId,
      entityType: approveModal.entityType,
      entityId: approveModal.entityId,
      remarks: remarks.trim() || undefined,
    });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            {t('back', { ns: 'common' })}
          </Button>
          <Title level={4} style={{ marginTop: 8, marginBottom: 0 }}>
            {t('inbox', { ns: 'approvals' })}
          </Title>
          <Text type="secondary">
            {inboxFilter === 'returned'
              ? t('noPending', { ns: 'approvals' })
              : inboxFilter === 'escalated'
                ? t('escalated', { ns: 'approvals' })
                : inboxFilter === 'overdue'
                  ? t('overdue', { ns: 'approvals' })
                  : t('pending', { ns: 'approvals' })}
          </Text>
        </div>

        {summary ? (
          <Alert
            type="info"
            showIcon
            message={`${summary.greeting} — ${summary.urgent} urgent · ${summary.escalated} escalated · ${summary.normal} normal`}
            description={`Estimated review time ~${summary.estimated_review_minutes} min${
              summary.highest_priority_title ? `. Top: ${summary.highest_priority_title}` : ''
            }`}
          />
        ) : null}

        <Card>
          <Space style={{ marginBottom: 16 }} wrap>
            <Segmented
              options={inboxFilterOptions}
              value={inboxFilter}
              onChange={(v) => setInboxFilter(v as InboxFilter)}
            />
            <Segmented
              options={[
                { label: t('sortImpact', { ns: 'approvals' }), value: 'impact' },
                { label: t('sortDue', { ns: 'approvals' }), value: 'due' },
                { label: t('sortNewest', { ns: 'approvals' }), value: 'created' },
              ]}
              value={sortMode}
              onChange={(v) => setSortMode(v as SortMode)}
            />
            <Button onClick={() => void refetch()} loading={isFetching}>
              {t('refresh', { ns: 'common' })}
            </Button>
          </Space>

          {grouped.length > 1 ? (
            <Collapse
              style={{ marginBottom: 16 }}
              items={grouped.map(([etype, list]) => ({
                key: etype,
                label: `${entityGroupLabel(etype)} (${list.length})`,
                children: (
                  <Table<InboxRow>
                    rowKey="id"
                    columns={columns}
                    dataSource={list}
                    scroll={{ x: 1280 }}
                    pagination={false}
                    size="small"
                  />
                ),
              }))}
            />
          ) : (
            <Spin spinning={isLoading}>
              <Table<InboxRow>
                rowKey="id"
                columns={columns}
                dataSource={rows}
                scroll={{ x: 1280 }}
                pagination={{ pageSize: 20, showSizeChanger: true }}
                locale={{
                  emptyText:
                    inboxFilter === 'returned'
                      ? 'No returned documents.'
                      : 'No pending approvals in your inbox.',
                }}
              />
            </Spin>
          )}
        </Card>
      </Space>

      <Drawer
        title="Notification timeline"
        open={!!timelineId}
        onClose={() => setTimelineId(null)}
        width={420}
      >
        <Spin spinning={timelineLoading}>
          {timeline.length === 0 ? (
            <Text type="secondary">No notification events logged yet.</Text>
          ) : (
            <Timeline
              items={timeline.map((t) => ({
                children: (
                  <div>
                    <Text strong>{t.approval_event_id}</Text>
                    <div>
                      <Text type="secondary">
                        {t.channel} · {t.status}
                        {t.failed_reason ? ` · ${t.failed_reason}` : ''}
                      </Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(t.created_at).format('DD MMM YYYY HH:mm')}
                    </Text>
                  </div>
                ),
              }))}
            />
          )}
        </Spin>
      </Drawer>

      <Modal
        title={t('approve', { ns: 'approvals' })}
        open={!!approveModal}
        onOk={confirmApprove}
        onCancel={() => {
          setApproveModal(null);
          setRemarks('');
        }}
        confirmLoading={approveMutation.isPending}
        okText={t('approve', { ns: 'approvals' })}
      >
        <Input.TextArea
          rows={3}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder={t('comment', { ns: 'approvals' })}
        />
      </Modal>

      <RejectionReasonModal
        open={!!rejectModal}
        title={t('reject', { ns: 'approvals' })}
        documentType={rejectModal?.entityType || 'purchase_order'}
        documentLabel={rejectModal?.documentNumber ? `Document ${rejectModal.documentNumber}` : undefined}
        confirmLoading={rejectMutation.isPending}
        onCancel={() => setRejectModal(null)}
        onConfirm={async (payload) => {
          if (!rejectModal) return;
          rejectMutation.mutate({
            instanceId: rejectModal.instanceId,
            entityType: rejectModal.entityType,
            entityId: rejectModal.entityId,
            body: payload,
          });
        }}
      />
    </div>
  );
};

export default ApprovalInboxPage;
